import { Campaign } from '@ospace/schemas'
import { useInfiniteQuery, useMutation, useQueries, useQuery } from 'react-query'

import { CampaignService } from '../services'
import { ProductService } from '../services/product'

export const useCampaigns = (enabled: boolean = true) => {
  return useQuery<Campaign[]>(['campaigns'], CampaignService.getCampaigns, { enabled })
}

export const useCampaign = (id: number) => {
  return useQuery(['campaign', id], () => CampaignService.getCampaign(id))
}

export const useProductLines = () => {
  return useQuery(['product-lines'], () => ProductService.listProductLines())
}

export const useClientCampaign = (clientId: number, enabled?: boolean) => {
  return useQuery(
    ['client-campaign', clientId],
    () => CampaignService.findCampaignsByClient(clientId),
    { enabled }
  )
}

export const useClientsCampaigns = (clientIds: number[]) => {
  const queries = clientIds.map((clientId) => {
    return {
      enabled: !!clientId,
      queryKey: ['clients-campaign', clientId],
      queryFn: () => CampaignService.findCampaignsByClient(clientId),
    }
  })

  return useQueries(queries)
}

export const useCampaignDashboard = (campaignId: number, intelType: string) => {
  return useQuery(
    ['campaign', campaignId + intelType],
    () => CampaignService.getDashboards(campaignId),
    {
      refetchOnMount: true,
    }
  )
}

export const usePipelineStatus = (campaignId: number, enabled: boolean = true) => {
  return useQuery(
    ['campaign-pipeline-status', campaignId],
    () => CampaignService.getDealPipelineStatus(campaignId),
    {
      enabled,
    }
  )
}

export const useDeals = (campaignId: number) => {
  return useQuery(['campaign-deals', campaignId], () => CampaignService.getDeals(campaignId))
}

export const useOpportunityStats = (campaignId: number, enabled: boolean = true) => {
  return useQuery(
    ['campaign-opportunity-stats', campaignId],
    () => CampaignService.getOpportunitiesStats(campaignId),
    {
      enabled,
    }
  )
}

export const useMCOstats = (campaignId: number, enabled: boolean = true) => {
  return useQuery(
    ['campaign-mco-stats', campaignId],
    () => CampaignService.getMCOStats(campaignId),
    {
      enabled,
    }
  )
}

export const useIntentReports = (campaignId: number, enabled: boolean = true) => {
  return useQuery(
    ['campaign-intent-reports', campaignId],
    () => CampaignService.getIntentReports(campaignId),
    {
      enabled,
    }
  )
}

export const useContactsAndNotes = (dealId: number, campaignId: number) => {
  return useQuery(
    ['deal-contacts-notes', dealId, campaignId],
    () => CampaignService.getContactsAndNotes(dealId, campaignId),
    {
      enabled: !!dealId,
    }
  )
}

export const useDealViewMutation = (dealId: number, campaignId: number) => {
  return useMutation(['deal-viewed', dealId, campaignId], () =>
    CampaignService.postDealViewedDate(dealId, campaignId)
  )
}

export const useExportLeads = () => {
  return useMutation(
    ['export-leads'],
    ({
      campaignId,
      hubspotOwnerId,
      exclusionListId,
      lemlistCampaignId,
      hasLeads,
    }: {
      campaignId: number
      hubspotOwnerId: string
      exclusionListId: string
      lemlistCampaignId: string
      hasLeads: boolean
    }) =>
      CampaignService.triggerLeadExport(
        campaignId,
        hubspotOwnerId,
        exclusionListId,
        lemlistCampaignId,
        hasLeads
      )
  )
}

export const useLeadExportFailedRows = (
  campaignId: number,
  exportId: string,
  isEnabled: boolean
) => {
  return useInfiniteQuery(
    ['lead-export-failed-rows', exportId],
    ({ pageParam }) =>
      CampaignService.getFailedRows(campaignId, exportId, {
        limit: 50,
        cursor: pageParam,
      }),
    {
      enabled: isEnabled,
      getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
      staleTime: Infinity,
      retry: 1,
    }
  )
}

export const useLeadExportStatus = (
  campaignId: number,
  exportId: string,
  opts?: { pollIntervalMs?: number; enabled?: boolean }
) => {
  const pollIntervalMs = opts?.pollIntervalMs ?? 5000
  const enabled = Boolean(opts?.enabled ?? (campaignId !== undefined && exportId))

  return useQuery(
    ['lead-export-status', exportId],
    () => CampaignService.getLeadExportStatus(campaignId, exportId),
    {
      enabled,
      refetchInterval: (data) => {
        // don't start polling until we have initial data
        if (!data) return false
        // stop polling when completed
        if (data.completedAt || data.status === 'completed') return false
        return pollIntervalMs
      },
      // keep polling while tab inactive if you want
      refetchIntervalInBackground: true,
      // make sure each poll actually hits the network (not just cache)
      refetchOnWindowFocus: true,
      retry: 1,
    }
  )
}

export const useLemlistCampaigns = (campaignId: number) => {
  return useQuery(['lemlist-campaigns', campaignId], () =>
    CampaignService.getLemlistCampaigns(campaignId)
  )
}

export const useHubspotUsers = () => {
  return useQuery(['hubspot-users'], () => CampaignService.getHubspotUsers())
}
